import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import DataTable, { DataTablePresentationProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        ordersDataView: getOrdersDataView(state, recentOrdersParameter),
        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        settingsCollection: getSettingsCollection(state),
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        language: state.context.session.language,
    };
};

const mapDispatchToProps = {
    loadOrders,
};

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderSummaryCardStyles {
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyPresentationProps;
    dataTable?: DataTablePresentationProps;
    dataTableHead?: DataTableHeadProps;
    tableHeaders?: DataTableHeaderPresentationProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    usernameCells?: DataTableCellProps;
    nameCells?: DataTableCellProps;
    emailCells?: DataTableCellProps;
    statusCells?: DataTableCellProps;
    roleCells?: DataTableCellProps;
    lastSignInCells?: DataTableCellProps;
    noOrdersFoundText?: TypographyPresentationProps;
    noOrdersFoundGridItem?: GridItemProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    viewAllButton?: InjectableCss;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    container: {
        css: css`
            overflow-x: auto;
            margin-bottom: 25px;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
    usernameCells: {
        css: css`
            font-weight: 400;
        `,
    },
    noOrdersFoundGridItem: {
        width: 12,
        css: css`
            padding: 0;
        `,
    },
    noOrdersFoundText: { variant: "h3" },
    dataTable: {
        css: css`
            border: 1px solid #ddd;
            th,
            td {
                border-right: 1px solid #ddd;
            }
        `,
    },
    tableHeaders: {
        css: css`
            border-bottom: 2px solid #d0d0d0;
            span[class^="TypographyStyle"] {
                font-size: 14px;
            }
        `,
    },
    dataTableBody: {
        css: css`
            tr,
            td,
            span[class^="TypographyStyle"] {
                font-size: 14px;
            }
        `,
    },
    titleGridItem: {
        width: 12,
        css: css`
            padding: 0 0 15px 0;
            justify-content: space-between;
        `,
    },
    titleText: {
        variant: "h3",
    },
    viewAllButton: {
        css: css`
            background-color: #999;
            padding: 7px 11px;
            :hover {
                opacity: 0.9;
                span {
                    text-decoration: none;
                }
            }
            span {
                color: #fff !important;
                font-size: 14px;
                font-weight: 700;
            }
        `,
    },
};

let recentOrdersParameter: GetOrdersApiParameter = {
    customerSequence: "-1",
    page: 1,
    sort: "OrderDate DESC",
};

const styles = orderSummaryCardStyles;

class RecentOrdersTable extends React.Component<Props> {
    componentDidMount() {
        recentOrdersParameter.pageSize = 5;
        if (this.props.canViewOrders && !this.props.ordersDataView.value) {
            this.props.loadOrders(recentOrdersParameter);
        }
    }

    render() {
        const { ordersDataView, orderHistoryPageLink, settingsCollection, canViewOrders } = this.props;
        if (!canViewOrders || !ordersDataView.value) {
            return null;
        }

        const orderHistoryUrl = orderHistoryPageLink ? orderHistoryPageLink.url : undefined;

        return (
            <>
                <StyledWrapper {...styles.container} data-test-selector="userListUsersTable">
                    <GridItem {...styles.titleGridItem}>
                        <Typography {...styles.titleText}>Recent Orders</Typography>
                        <Link href={orderHistoryUrl} {...styles.viewAllButton}>
                            View All
                        </Link>
                    </GridItem>
                    {ordersDataView.value.length === 0 && (
                        <GridItem className="no-orders-found-grid-item" {...styles.noOrdersFoundGridItem}>
                            <Typography
                                className="no-orders-found-text"
                                {...styles.noOrdersFoundText}
                                data-test-selector="recentOrders_NoOrdersFound"
                            >
                                {translate("No orders found")}
                            </Typography>
                        </GridItem>
                    )}
                    {ordersDataView.value.length !== 0 && (
                        <DataTable {...styles.dataTable}>
                            <DataTableHead {...styles.dataTableHead}>
                                <DataTableHeader
                                    {...styles.tableHeaders}
                                    data-test-selector="userListUsersTable_usernameHeader"
                                >
                                    {translate("Date")}
                                </DataTableHeader>
                                <DataTableHeader
                                    {...styles.tableHeaders}
                                    data-test-selector="userListUsersTable_nameHeader"
                                >
                                    {translate("Order #")}
                                </DataTableHeader>
                                <DataTableHeader
                                    {...styles.tableHeaders}
                                    data-test-selector="userListUsersTable_emailHeader"
                                >
                                    {translate("Ship To / Pick Up")}
                                </DataTableHeader>
                                <DataTableHeader
                                    {...styles.tableHeaders}
                                    data-test-selector="userListUsersTable_statusHeader"
                                >
                                    {translate("Status")}
                                </DataTableHeader>
                                <DataTableHeader
                                    {...styles.tableHeaders}
                                    data-test-selector="userListUsersTable_roleHeader"
                                >
                                    {translate("Web Order #")}
                                </DataTableHeader>
                                <DataTableHeader
                                    {...styles.tableHeaders}
                                    data-test-selector="userListUsersTable_lastSignInHeader"
                                >
                                    {translate("PO #")}
                                </DataTableHeader>
                                <DataTableHeader
                                    {...styles.tableHeaders}
                                    data-test-selector="userListUsersTable_lastSignInHeader"
                                >
                                    {translate("Total")}
                                </DataTableHeader>
                            </DataTableHead>
                            <DataTableBody {...styles.dataTableBody}>
                                {ordersDataView.value.map(order => {
                                    if (!order) {
                                        return null;
                                    }

                                    const orderDateDisplay = getLocalizedDateTime({
                                        dateTime: order.orderDate,
                                        language: this.props.language,
                                        options: {
                                            year: "numeric",
                                            month: "numeric",
                                            day: "numeric",
                                        },
                                    });

                                    const orderNumber = order.erpOrderNumber || order.webOrderNumber;
                                    return (
                                        <DataTableRow
                                            {...styles.dataTableRow}
                                            key={order.id}
                                            data-test-selector="userListUsersTable_row"
                                            data-test-key={orderNumber}
                                        >
                                            <DataTableCell
                                                {...styles.usernameCells}
                                                as="th"
                                                scope="row"
                                                data-test-selector="userListUsersTable_cell_username"
                                                data-test-key={order.id}
                                            >
                                                {orderNumber && (
                                                    <OrderDetailPageTypeLink
                                                        title={orderDateDisplay}
                                                        orderNumber={orderNumber}
                                                    />
                                                )}
                                            </DataTableCell>
                                            <DataTableCell
                                                {...styles.nameCells}
                                                data-test-selector="userListUsersTable_cell_name"
                                                data-test-key={order.id}
                                            >
                                                {order.erpOrderNumber}
                                            </DataTableCell>
                                            <DataTableCell
                                                {...styles.emailCells}
                                                data-test-selector="userListUsersTable_cell_email"
                                                data-test-key={order.id}
                                            >
                                                {order.stCompanyName}
                                            </DataTableCell>
                                            <DataTableCell
                                                {...styles.statusCells}
                                                data-test-selector="userListUsersTable_cell_status"
                                                data-test-key={order.id}
                                            >
                                                {order.statusDisplay}
                                            </DataTableCell>
                                            <DataTableCell
                                                {...styles.roleCells}
                                                data-test-selector="userListUsersTable_cell_role"
                                                data-test-key={order.id}
                                            >
                                                {order.webOrderNumber}
                                            </DataTableCell>
                                            <DataTableCell
                                                {...styles.lastSignInCells}
                                                data-test-selector="userListUsersTable_cell_lastSignIn"
                                                data-test-key={order.id}
                                            >
                                                {order.customerPO}
                                            </DataTableCell>
                                            <DataTableCell
                                                {...styles.lastSignInCells}
                                                data-test-selector="userListUsersTable_cell_lastSignIn"
                                                data-test-key={order.id}
                                            >
                                                {order.orderGrandTotalDisplay}
                                            </DataTableCell>
                                        </DataTableRow>
                                    );
                                })}
                            </DataTableBody>
                        </DataTable>
                    )}
                </StyledWrapper>
            </>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentOrdersTable),
    definition: {
        group: "Common",
        icon: "list-ul",
        displayName: "Recent Orders Table View",
    },
};

export default widgetModule;
